import { useState, useEffect, useCallback } from 'react';
import { models } from 'powerbi-client';
import {
    POWERBI_SERVICE,
    buildPowerBiFilters,
    EMBED_STATUS,
    PANE_ENABLE_DELAY_MS,
    EMBED_TIMEOUT_MS,
    TOKEN_REFRESH_MARGIN_MS,
} from '../utils/dashboardHelpers';
import { clearEmbedToken } from './useEmbedToken';

const EMBED_STATUS_TEXT = {
    [EMBED_STATUS.REQUESTING]: 'Requesting dashboard access...',
    [EMBED_STATUS.RENDERING]: 'Rendering dashboard...',
};
const TIMEOUT_ERROR_TEXT = 'The dashboard is taking too long to load. Please try again.';
const GENERIC_ERROR_TEXT = 'The dashboard failed to load.';

export const usePowerBiEmbed = ({ containerRef, reportId, filters, getEmbedInfo }) => {
    const [status, setStatus] = useState(EMBED_STATUS.REQUESTING);
    const [error, setError] = useState(null);
    const [embedAttempt, setEmbedAttempt] = useState(0);

    const retry = useCallback(() => {
        clearEmbedToken(reportId);
        setEmbedAttempt((attempt) => attempt + 1);
    }, [reportId]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !reportId) return undefined;

        let cancelled = false;
        let settled = false;
        let report = null;
        const timers = [];

        setStatus(EMBED_STATUS.REQUESTING);
        setError(null);

        const failWith = (message) => {
            if (cancelled || settled) return;
            settled = true;
            setError(message);
            setStatus(EMBED_STATUS.ERROR);
        };

        const scheduleTokenRefresh = (expiresAt) => {
            const delay = new Date(expiresAt).getTime() - Date.now() - TOKEN_REFRESH_MARGIN_MS;
            if (delay <= 0) return;

            const refreshToken = async () => {
                try {
                    const embedInfo = await getEmbedInfo(reportId, { forceRefresh: true });
                    if (cancelled || !report) return;
                    await report.setAccessToken(embedInfo.embedToken);
                    scheduleTokenRefresh(embedInfo.expiresAt);
                } catch (ex) {
                    console.error('Dashboard token refresh failed:', ex);
                }
            };
            timers.push(setTimeout(refreshToken, delay));
        };

        const enablePanes = () => {
            const applyPaneSettings = () => {
                if (cancelled || !report) return;
                report
                    .updateSettings({ navContentPaneEnabled: true, filterPaneEnabled: true })
                    .catch(() => {});
            };
            timers.push(setTimeout(applyPaneSettings, PANE_ENABLE_DELAY_MS));
        };

        const embed = async () => {
            try {
                const embedInfo = await getEmbedInfo(reportId);
                if (cancelled) return;

                setStatus(EMBED_STATUS.RENDERING);

                report = POWERBI_SERVICE.embed(container, {
                    type: 'report',
                    tokenType: models.TokenType.Embed,
                    accessToken: embedInfo.embedToken,
                    embedUrl: embedInfo.embedUrl,
                    id: embedInfo.reportId,
                    permissions: models.Permissions.Read,
                    viewMode: models.ViewMode.View,
                    settings: {
                        background: models.BackgroundType.Transparent,
                        navContentPaneEnabled: false,
                        filterPaneEnabled: false,
                    },
                    filters: buildPowerBiFilters(filters),
                });

                report.on('loaded', enablePanes);
                report.on('rendered', () => {
                    if (cancelled || settled) return;
                    settled = true;
                    setStatus(EMBED_STATUS.RENDERED);
                });
                report.on('error', (event) => {
                    failWith(event?.detail?.message || GENERIC_ERROR_TEXT);
                });

                timers.push(setTimeout(() => failWith(TIMEOUT_ERROR_TEXT), EMBED_TIMEOUT_MS));
                scheduleTokenRefresh(embedInfo.expiresAt);
            } catch (ex) {
                failWith(ex.message || GENERIC_ERROR_TEXT);
            }
        };

        embed();

        return () => {
            cancelled = true;
            timers.forEach(clearTimeout);
            if (report) {
                report.off('loaded');
                report.off('rendered');
                report.off('error');
            }
            POWERBI_SERVICE.reset(container);
        };
    }, [containerRef, reportId, filters, getEmbedInfo, embedAttempt]);

    return { status, statusText: EMBED_STATUS_TEXT[status], error, retry };
};
